import { safeJson } from "src/app/helpers/Index";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import React from "react";
import { ResizeImage } from "src/app/helpers/ResizeImage";
import { Col, Row } from "react-bootstrap";
import { Sponsor } from "src/app/interfaces/Index";
import { FileService, ScoreboardService, SponsorService } from "src/app/services/Index";
import { ScoreboardConfigService } from "src/app/services/ScoreboardConfigService";
import { LayoutPreview } from "src/app/components/LayoutPreview";
import { environment } from "src/environments/environment";

interface LayoutImageItem { x: number; y: number; width: number; height: number }
interface LayoutTextItem { x: number; y: number; fontSize: number; color?: string | null }

/** Positions/sizes of the main scoreboard elements on the board canvas. */
interface ScoreboardLayout {
	canvasWidth: number;
	canvasHeight: number;
	leagueLogo: LayoutImageItem;
	homeBadge: LayoutImageItem;
	awayBadge: LayoutImageItem;
	mainSponsor: LayoutImageItem;
	score: LayoutTextItem;
	timer: LayoutTextItem;
	additionalTime: LayoutTextItem;
}

/** One positionable element of an info screen. */
interface ScreenElement { x: number; y: number; width?: number; height?: number; fontSize?: number; color?: string | null }

/** An info screen's config: background override + element geometry. */
interface ScreenConfig {
	useMainBackground: boolean;
	backgroundUrl?: string | null;
	showSponsor: boolean;
	animate?: boolean;
	showNextUp?: boolean;
	showStandings?: boolean;
	useSponsorText?: boolean;
	showHomeImages?: boolean;
	showAwayImages?: boolean;
	sponsorId?: number | null;
	elements: { [key: string]: ScreenElement };
}

interface ScoreboardTheme {
	backgroundUrl: string;
	leagueLogoUrl: string;
	additionalTimeLogoUrl: string;
	additionalTimeSponsorBg: string;
	mainSponsorId: number | null;
	additionalTimeSponsorId: number | null;
	mainSponsorBg: string;
	textColor: string;
	screenCycleSeconds: number;
	autoRotateEnabled: boolean;
	autoRotateScreens: string[];
	autoRotateDwellSeconds: number;
	layout: ScoreboardLayout;
	screens: { [key: string]: ScreenConfig };
}

/**
 * The editable screens, their elements and default geometry — mirrors the
 * board app's SCREEN_DEFAULTS (ScoreboardV2 screenLayoutStore) so an untouched
 * screen matches the board's built-in rendering exactly. text: centre-anchored
 * X/Y/Font/Colour; otherwise a top-left X/Y/W/H box.
 */
const SCREEN_DEFS: { key: string; label: string; sponsorToggle?: boolean; sponsorSlot?: boolean; sponsorTextOption?: boolean; playerImages?: boolean; animToggle?: boolean; nextUpToggle?: boolean; standingsToggle?: boolean; elements: { key: string; label: string; text?: boolean }[]; defaults: { [k: string]: ScreenElement } }[] = [
	{
		key: "goal", label: "Goal", sponsorToggle: true, playerImages: true, animToggle: true,
		elements: [
			{ key: "heading", label: "GOAL Heading", text: true },
			{ key: "playerName", label: "Player Name", text: true },
			{ key: "sponsor", label: "Sponsor Block" },
			{ key: "playerImage", label: "Player Image" },
		],
		defaults: {
			heading: { x: 288, y: 60, fontSize: 100 },
			playerName: { x: 150, y: 200, fontSize: 35 },
			sponsor: { x: 30, y: 260, width: 240, height: 110 },
			playerImage: { x: 320, y: 122, width: 250, height: 300 },
		},
	},
	{
		key: "card", label: "Card", sponsorToggle: true, sponsorSlot: true,
		elements: [
			{ key: "bannerText", label: "Banner Text", text: true },
			{ key: "playerName", label: "Player Name", text: true },
			{ key: "cardGraphic", label: "Card Graphic" },
			{ key: "sponsorImage", label: "Sponsor Image" },
		],
		defaults: {
			bannerText: { x: 288, y: 37, fontSize: 40 },
			playerName: { x: 144, y: 230, fontSize: 40 },
			cardGraphic: { x: 352, y: 130, width: 160, height: 230 },
			sponsorImage: { x: 0, y: 367, width: 576, height: 55 },
		},
	},
	{
		key: "sub", label: "Substitution", sponsorToggle: true, sponsorSlot: true, playerImages: true,
		elements: [
			{ key: "heading", label: "Heading", text: true },
			{ key: "offImage", label: "Player Off Image" },
			{ key: "onImage", label: "Player On Image" },
			{ key: "sponsorImage", label: "Sponsor Bar" },
		],
		defaults: {
			heading: { x: 288, y: 30, fontSize: 40 },
			offImage: { x: 16, y: 145, width: 260, height: 235 },
			onImage: { x: 300, y: 145, width: 260, height: 235 },
			sponsorImage: { x: 0, y: 382, width: 576, height: 50 },
		},
	},
	{
		key: "team", label: "Team", sponsorToggle: true, sponsorTextOption: true, playerImages: true, animToggle: true,
		elements: [
			{ key: "heading", label: "Heading", text: true },
			{ key: "playerName", label: "Player Name", text: true },
			{ key: "sponsor", label: "Sponsor Block" },
			{ key: "mugshot", label: "Mugshot" },
		],
		defaults: {
			heading: { x: 288, y: 35, fontSize: 40 },
			playerName: { x: 144, y: 120, fontSize: 40 },
			sponsor: { x: 10, y: 330, width: 250, height: 95 },
			mugshot: { x: 176, y: 92, width: 400, height: 340 },
		},
	},
	{
		key: "partners", label: "Partners",
		elements: [
			{ key: "title", label: "Partnership Title", text: true },
			{ key: "logo", label: "Partner Logo" },
		],
		defaults: {
			title: { x: 288, y: 42, fontSize: 40 },
			logo: { x: 43, y: 100, width: 490, height: 300 },
		},
	},
	{
		key: "general", label: "General", nextUpToggle: true, standingsToggle: true,
		elements: [
			{ key: "heading", label: "Heading", text: true },
			{ key: "homeBadge", label: "Home Badge" },
			{ key: "awayBadge", label: "Away Badge" },
			{ key: "vsText", label: "“v” Text", text: true },
			{ key: "leagueLogo", label: "League Logo" },
			{ key: "dateText", label: "Date Text", text: true },
		],
		defaults: {
			heading: { x: 288, y: 95, fontSize: 38 },
			homeBadge: { x: 90, y: 130, width: 170, height: 170 },
			awayBadge: { x: 316, y: 130, width: 170, height: 170 },
			vsText: { x: 288, y: 215, fontSize: 40 },
			leagueLogo: { x: 208, y: 318, width: 160, height: 40 },
			dateText: { x: 288, y: 372, fontSize: 30 },
		},
	},
	{
		key: "attendance", label: "Attendance", sponsorToggle: true, sponsorSlot: true, animToggle: true,
		elements: [
			{ key: "title", label: "Title", text: true },
			{ key: "total", label: "Total Gate", text: true },
			{ key: "away", label: "Away Count", text: true },
			{ key: "sponsorImage", label: "Sponsor Image" },
		],
		defaults: {
			title: { x: 288, y: 120, fontSize: 54 },
			total: { x: 288, y: 200, fontSize: 130 },
			away: { x: 288, y: 272, fontSize: 46 },
			sponsorImage: { x: 0, y: 367, width: 576, height: 55 },
		},
	},
	{
		key: "halftimeDraw", label: "Half-Time Draw", sponsorToggle: true, sponsorSlot: true, animToggle: true,
		elements: [
			{ key: "title", label: "Title", text: true },
			{ key: "rows", label: "Prize Rows (first line)", text: true },
			{ key: "sponsorImage", label: "Sponsor Image" },
		],
		defaults: {
			title: { x: 288, y: 130, fontSize: 46 },
			rows: { x: 288, y: 195, fontSize: 40 },
			sponsorImage: { x: 0, y: 367, width: 576, height: 55 },
		},
	},
	{
		key: "additionalTime", label: "Additional Time", sponsorToggle: true, sponsorSlot: true, animToggle: true,
		elements: [
			{ key: "title", label: "Title", text: true },
			{ key: "subtitle", label: "Subtitle", text: true },
			{ key: "minutes", label: "Minutes", text: true },
			{ key: "sponsorImage", label: "Sponsor Bar" },
		],
		defaults: {
			title: { x: 288, y: 100, fontSize: 54 },
			subtitle: { x: 288, y: 150, fontSize: 34 },
			minutes: { x: 288, y: 240, fontSize: 130 },
			sponsorImage: { x: 0, y: 332, width: 576, height: 100 },
		},
	},
	{
		key: "penalties", label: "Penalties",
		elements: [
			{ key: "title", label: "Title", text: true },
			{ key: "score", label: "Shootout Score", text: true },
			{ key: "homeBadge", label: "Home Badge" },
			{ key: "homeRow", label: "Home Kicks Row" },
			{ key: "awayBadge", label: "Away Badge" },
			{ key: "awayRow", label: "Away Kicks Row" },
		],
		defaults: {
			title: { x: 288, y: 45, fontSize: 46 },
			score: { x: 288, y: 110, fontSize: 66 },
			homeBadge: { x: 20, y: 170, width: 60, height: 60 },
			homeRow: { x: 100, y: 178, width: 460, height: 44 },
			awayBadge: { x: 20, y: 260, width: 60, height: 60 },
			awayRow: { x: 100, y: 268, width: 460, height: 44 },
		},
	},
	{
		key: "potm", label: "POTM", sponsorToggle: true, sponsorSlot: true, playerImages: true,
		elements: [
			{ key: "title", label: "Title", text: true },
			{ key: "playerName", label: "Player Name", text: true },
			{ key: "sponsor", label: "Sponsor Block" },
			{ key: "mugshot", label: "Mugshot" },
		],
		defaults: {
			title: { x: 288, y: 40, fontSize: 50 },
			playerName: { x: 144, y: 120, fontSize: 44 },
			sponsor: { x: 10, y: 320, width: 260, height: 100 },
			mugshot: { x: 206, y: 92, width: 350, height: 340 },
		},
	},
	{
		// No positionable elements - registered so the intro-slide sponsor can
		// be assigned and the page animations toggled.
		key: "latestScores", label: "Latest Scores", sponsorSlot: true, animToggle: true,
		elements: [],
		defaults: {},
	},
];

/**
 * Stand-in text for the preview, so it reads like the real screen rather than
 * a wall of element names. Keyed "screen.element".
 */
const SAMPLE_TEXT: { [key: string]: string } = {
	"goal.heading": "GOAL!!!",
	"goal.playerName": "J. SMITH",
	"card.bannerText": "YELLOW CARD",
	"card.playerName": "J. SMITH",
	"sub.heading": "IRON - SUBSTITUTION",
	"team.heading": "IRON - STARTING",
	"team.playerName": "9. J. SMITH",
	"partners.title": "OFFICIAL PARTNER",
	"general.heading": "NEXT UP AT GLANFORD PARK",
	"general.vsText": "v",
	"general.dateText": "SATURDAY 9 AUGUST - 3PM",
	"attendance.title": "ATTENDANCE",
	"attendance.total": "8,412",
	"attendance.away": "(412 Away)",
	"halftimeDraw.title": "HALF TIME DRAW NUMBERS",
	"halftimeDraw.rows": "1st - 13402 - £250",
	"additionalTime.title": "ADDITIONAL TIME",
	"additionalTime.subtitle": "A minimum of...",
	"additionalTime.minutes": "+4",
	"penalties.title": "PENALTIES",
	"penalties.score": "3 - 2",
	"potm.title": "PLAYER OF THE MATCH",
	"potm.playerName": "9. J. SMITH"
};

/** V1 board geometry — the seed values before a club customises anything. */
const DEFAULT_LAYOUT: ScoreboardLayout = {
	canvasWidth: 576,
	canvasHeight: 432,
	leagueLogo: { x: 0, y: 0, width: 576, height: 75 },
	homeBadge: { x: 20, y: 158, width: 190, height: 190 },
	awayBadge: { x: 366, y: 158, width: 190, height: 190 },
	mainSponsor: { x: 0, y: 367, width: 576, height: 55 },
	score: { x: 290, y: 245, fontSize: 65, color: null },
	timer: { x: 289, y: 130, fontSize: 85, color: null },
	additionalTime: { x: 288, y: 38, fontSize: 60, color: null },
};

const SCREEN_OPTIONS = [
	"Scoreboard", "Latest", "Partners", "Halftime Draw", "Additional Time",
	"Attendance", "Team", "General", "Latest Scores", "POTM", "Penalties",
].map((s) => ({ label: s, value: s }));

type Props = {};
type State = {
	theme: ScoreboardTheme;
	sponsors: Sponsor[];
	isLoading: boolean;
	saving: boolean;
	activeScreen: string;
	configs: any[];
	configId: number | null;
	showAddConfig: boolean;
	newConfigName: string;
	duplicateFrom: boolean;
};

const EMPTY_THEME: ScoreboardTheme = {
	backgroundUrl: "",
	leagueLogoUrl: "",
	additionalTimeLogoUrl: "",
	additionalTimeSponsorBg: "#2A3247",
	mainSponsorId: null,
	additionalTimeSponsorId: null,
	mainSponsorBg: "#FFFFFF",
	textColor: "#FFFFFF",
	screenCycleSeconds: 5,
	autoRotateEnabled: false,
	autoRotateScreens: [],
	autoRotateDwellSeconds: 15,
	layout: DEFAULT_LAYOUT,
	screens: {},
};

/**
 * Scoreboard display settings: the web-managed theme the board app downloads on
 * start. Upload the background and logos, pick which sponsor fills each slot,
 * and set the additional-time bar colour.
 */
export class AdminScoreboardSettings extends React.Component<Props, State> {
	toast: any;

	constructor(props: Props) {
		super(props);
		this.state = { theme: { ...EMPTY_THEME }, sponsors: [], isLoading: false, saving: false, activeScreen: "main", configs: [], configId: null, showAddConfig: false, newConfigName: "", duplicateFrom: true };
	}

	componentDidMount() {
		this.getConfigs();
		this.getTheme();
		this.getSponsors();
	}

	/** The club's named layouts. Absent on pre-config APIs, which is fine. */
	async getConfigs() {
		try {
			const res = await safeJson(ScoreboardConfigService.getMine());
			if (Array.isArray(res) && res.length) {
				const current = res.find((c: any) => c.isDefault) || res[0];
				this.setState({ configs: res, configId: this.state.configId ?? current.id });
			}
		} catch { /* pre-config API */ }
	}

	async selectConfig(configId: number) {
		this.setState({ configId }, () => this.getTheme());
	}

	async addConfig() {
		const name = this.state.newConfigName.trim();
		if (!name) return;
		const res = await ScoreboardConfigService.add(name, this.state.duplicateFrom && this.state.configId ? this.state.configId : undefined);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "Please try again later.", life: 5000, severity: "error", summary: "Couldn't create the layout" });
			return;
		}
		const created = await res.json();
		this.setState({ showAddConfig: false, newConfigName: "", configId: created.id }, async () => {
			await this.getConfigs();
			this.getTheme();
		});
	}

	async renameConfig() {
		const config = this.state.configs.find((c) => c.id === this.state.configId);
		if (!config) return;
		const name = window.prompt("Rename layout", config.name);
		if (!name || !name.trim()) return;
		await ScoreboardConfigService.rename(config.id, name.trim());
		this.getConfigs();
	}

	async makeDefault() {
		if (!this.state.configId) return;
		await ScoreboardConfigService.setDefault(this.state.configId);
		this.getConfigs();
		this.toast.show({ closable: false, detail: "Boards without their own layout will use this one.", life: 4000, severity: "success", summary: "Default layout set" });
	}

	async removeConfig() {
		const config = this.state.configs.find((c) => c.id === this.state.configId);
		if (!config) return;
		if (!window.confirm(`Remove the "${config.name}" layout? Boards using it fall back to the default.`)) return;
		const res = await ScoreboardConfigService.remove(config.id);
		if (!res.ok) {
			this.toast.show({ closable: false, detail: "The default layout can't be removed — make another one the default first.", life: 6000, severity: "warn", summary: "Can't remove" });
			return;
		}
		this.setState({ configId: null }, async () => {
			await this.getConfigs();
			this.getTheme();
		});
	}

	async getTheme() {
		this.setState({ isLoading: true });
		try {
			const res = await safeJson(ScoreboardService.getTheme(this.state.configId ?? undefined));
			this.setState({ theme: { ...EMPTY_THEME, ...res, layout: { ...DEFAULT_LAYOUT, ...(res.layout || {}) }, screens: res.screens || {} }, isLoading: false });
		} catch {
			this.setState({ isLoading: false });
		}
	}

	async getSponsors() {
		const res = await safeJson(SponsorService.getAll());
		if (Array.isArray(res)) this.setState({ sponsors: res });
	}

	inputChange(value: any, name: keyof ScoreboardTheme) {
		this.setState({ theme: { ...this.state.theme, [name]: value } });
	}

	layoutChange(element: keyof ScoreboardLayout, field: string, value: number | string | null) {
		const layout = { ...(this.state.theme.layout || DEFAULT_LAYOUT) };
		layout[element] = { ...(layout[element] as any), [field]: value };
		this.setState({ theme: { ...this.state.theme, layout } });
	}

	canvasChange(field: "canvasWidth" | "canvasHeight", value: number) {
		const layout = { ...(this.state.theme.layout || DEFAULT_LAYOUT), [field]: value };
		this.setState({ theme: { ...this.state.theme, layout } });
	}

	/** A screen's config with defaults filled in for anything unsaved. */
	mergedScreen(key: string): ScreenConfig {
		const def = SCREEN_DEFS.find((d) => d.key === key);
		const saved = this.state.theme.screens[key];
		return {
			useMainBackground: saved?.useMainBackground ?? true,
			backgroundUrl: saved?.backgroundUrl || null,
			showSponsor: saved?.showSponsor ?? true,
			animate: saved?.animate ?? true,
			showNextUp: saved?.showNextUp ?? true,
			showStandings: saved?.showStandings ?? true,
			useSponsorText: saved?.useSponsorText ?? false,
			showHomeImages: saved?.showHomeImages ?? true,
			showAwayImages: saved?.showAwayImages ?? true,
			sponsorId: saved?.sponsorId ?? null,
			elements: { ...(def?.defaults || {}), ...(saved?.elements || {}) },
		};
	}

	screenChange(key: string, patch: Partial<ScreenConfig>) {
		const merged = { ...this.mergedScreen(key), ...patch };
		this.setState({ theme: { ...this.state.theme, screens: { ...this.state.theme.screens, [key]: merged } } });
	}

	screenElementChange(key: string, elKey: string, field: string, value: number | string | null) {
		const merged = this.mergedScreen(key);
		merged.elements = { ...merged.elements, [elKey]: { ...merged.elements[elKey], [field]: value } };
		this.screenChange(key, merged);
	}

	async uploadScreenBackground(data: any, key: string) {
		const uploadData = new FormData();
		uploadData.append("containerName", "uploads");
		uploadData.append("files", await ResizeImage(data.files[0]));
		uploadData.append("mimeTypeKey", "Images");
		const response = await safeJson(FileService.upload(uploadData));
		this.screenChange(key, { backgroundUrl: `${environment.server.storageUrl}/${response[0].location}` });
		this.toast.show({ closable: false, detail: "Background uploaded", life: 3000, severity: "success", summary: "Uploaded" });
	}

	async uploadImage(data: any, property: keyof ScoreboardTheme) {
		const uploadData = new FormData();
		uploadData.append("containerName", "uploads");
		uploadData.append("files", await ResizeImage(data.files[0]));
		uploadData.append("mimeTypeKey", "Images");
		const response = await safeJson(FileService.upload(uploadData));
		this.inputChange(`${environment.server.storageUrl}/${response[0].location}`, property);
		this.toast.show({ closable: false, detail: "Image uploaded", life: 3000, severity: "success", summary: "Uploaded" });
	}

	async save() {
		this.setState({ saving: true });
		try {
			const res = await ScoreboardService.saveTheme(this.state.theme, this.state.configId ?? undefined);
			if (!res.ok) throw new Error();
			const saved = await res.json();
			this.setState({ theme: { ...EMPTY_THEME, ...saved, layout: { ...DEFAULT_LAYOUT, ...(saved.layout || {}) }, screens: saved.screens || {} }, saving: false });
			this.toast.show({ closable: false, detail: "Scoreboard theme saved — connected boards update instantly.", life: 5000, severity: "success", summary: "Saved" });
		} catch {
			this.setState({ saving: false });
			this.toast.show({ closable: false, detail: "Please try again later.", life: 5000, severity: "error", summary: "Something went wrong!" });
		}
	}

	layoutNum(element: keyof ScoreboardLayout, field: string, label: string) {
		const item = (this.state.theme.layout || DEFAULT_LAYOUT)[element] as any;
		return (
			<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
				<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>{label}</label>
				<InputNumber inputStyle={{ width: 74 }} value={item[field] ?? 0} onValueChange={(e) => this.layoutChange(element, field, e.value ?? 0)} />
			</div>
		);
	}

	layoutColor(element: keyof ScoreboardLayout) {
		const item = (this.state.theme.layout || DEFAULT_LAYOUT)[element] as LayoutTextItem;
		const effective = item.color || this.state.theme.textColor || "#FFFFFF";
		return (
			<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
				<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>Colour</label>
				<input title={item.color ? "Element colour" : "Inheriting the theme text colour"} type="color" value={effective} onChange={(e) => this.layoutChange(element, "color", e.target.value)} />
				{item.color && <Button className="p-button-text" label="inherit" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => this.layoutChange(element, "color", null)} />}
			</div>
		);
	}

	layoutRow(label: string, element: keyof ScoreboardLayout, isText: boolean) {
		return (
			<div style={{ alignItems: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.08)", display: "flex", flexWrap: "wrap", gap: 14, padding: "10px 0" }}>
				<span style={{ fontWeight: 700, width: 150 }}>{label}</span>
				{this.layoutNum(element, "x", "X")}
				{this.layoutNum(element, "y", "Y")}
				{isText
					? <>
						{this.layoutNum(element, "fontSize", "Font")}
						{this.layoutColor(element)}
					</>
					: <>
						{this.layoutNum(element, "width", "W")}
						{this.layoutNum(element, "height", "H")}
					</>}
			</div>
		);
	}

	screenNum(screenKey: string, elKey: string, field: string, label: string) {
		const el = this.mergedScreen(screenKey).elements[elKey] as any;
		return (
			<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
				<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>{label}</label>
				<InputNumber inputStyle={{ width: 74 }} value={el?.[field] ?? 0} onValueChange={(e) => this.screenElementChange(screenKey, elKey, field, e.value ?? 0)} />
			</div>
		);
	}

	screenColor(screenKey: string, elKey: string) {
		const el = this.mergedScreen(screenKey).elements[elKey];
		const effective = el?.color || this.state.theme.textColor || "#FFFFFF";
		return (
			<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
				<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>Colour</label>
				<input title={el?.color ? "Element colour" : "Inheriting the theme text colour"} type="color" value={effective} onChange={(e) => this.screenElementChange(screenKey, elKey, "color", e.target.value)} />
				{el?.color && <Button className="p-button-text" label="inherit" style={{ fontSize: 11, padding: "2px 6px" }} onClick={() => this.screenElementChange(screenKey, elKey, "color", null)} />}
			</div>
		);
	}

	/** Preview of the main scoreboard screen. */
	renderMainPreview() {
		const layout = this.state.theme.layout || DEFAULT_LAYOUT;
		return (
			<LayoutPreview
				background={this.state.theme.backgroundUrl}
				canvasHeight={layout.canvasHeight}
				canvasWidth={layout.canvasWidth}
				elements={[
					{ el: layout.leagueLogo, key: "leagueLogo", label: "League Logo" },
					{ el: layout.homeBadge, key: "homeBadge", label: "Home Badge" },
					{ el: layout.awayBadge, key: "awayBadge", label: "Away Badge" },
					{ el: layout.mainSponsor, key: "mainSponsor", label: "Sponsor" },
					{ el: layout.timer, key: "timer", label: "Timer", sample: "88:88", text: true },
					{ el: layout.score, key: "score", label: "Score", sample: "0-0", text: true },
					{ el: layout.additionalTime, key: "additionalTime", label: "Additional Time", sample: "ADDITIONAL TIME", text: true }
				]}
				textColor={this.state.theme.textColor}
			/>
		);
	}

	/** Preview of one info screen. */
	renderScreenPreview(def: typeof SCREEN_DEFS[number]) {
		const cfg = this.mergedScreen(def.key);
		const layout = this.state.theme.layout || DEFAULT_LAYOUT;
		const background = cfg.useMainBackground ? this.state.theme.backgroundUrl : (cfg.backgroundUrl || "");
		return (
			<LayoutPreview
				background={background}
				canvasHeight={layout.canvasHeight}
				canvasWidth={layout.canvasWidth}
				elements={def.elements.map((e) => ({
					el: cfg.elements[e.key],
					key: e.key,
					label: e.label,
					sample: SAMPLE_TEXT[`${def.key}.${e.key}`] || e.label.toUpperCase(),
					text: e.text
				}))}
				textColor={this.state.theme.textColor}
			/>
		);
	}

	renderScreenEditor(def: typeof SCREEN_DEFS[number]) {
		const cfg = this.mergedScreen(def.key);
		return (
			<div key={def.key}>
				<div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 26, marginBottom: 14 }}>
					<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
						<InputSwitch checked={cfg.useMainBackground} onChange={(e) => this.screenChange(def.key, { useMainBackground: e.value })} />
						<label style={{ fontWeight: 600 }}>Use main background</label>
					</div>
					{def.sponsorToggle &&
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.showSponsor} onChange={(e) => this.screenChange(def.key, { showSponsor: e.value })} />
							<label style={{ fontWeight: 600 }}>Show sponsor</label>
						</div>}
					{def.animToggle &&
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.animate !== false} onChange={(e) => this.screenChange(def.key, { animate: e.value })} />
							<label style={{ fontWeight: 600 }}>Entrance animations</label>
						</div>}
					{def.nextUpToggle &&
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.showNextUp !== false} onChange={(e) => this.screenChange(def.key, { showNextUp: e.value })} />
							<label style={{ fontWeight: 600 }}>Show &ldquo;Next Up&rdquo; slide</label>
						</div>}
					{def.standingsToggle &&
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.showStandings !== false} onChange={(e) => this.screenChange(def.key, { showStandings: e.value })} />
							<label style={{ fontWeight: 600 }}>Show league table slide</label>
						</div>}
					{def.sponsorTextOption && cfg.showSponsor &&
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={!!cfg.useSponsorText} onChange={(e) => this.screenChange(def.key, { useSponsorText: e.value })} />
							<label style={{ fontWeight: 600 }}>Use player sponsor text</label>
						</div>}
				</div>
				{def.playerImages &&
					<div style={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 26, marginBottom: 16 }}>
						<span style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 700, textTransform: "uppercase" }}>Player photos</span>
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.showHomeImages !== false} onChange={(e) => this.screenChange(def.key, { showHomeImages: e.value })} />
							<label style={{ fontWeight: 600 }}>Home team</label>
						</div>
						<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
							<InputSwitch checked={cfg.showAwayImages !== false} onChange={(e) => this.screenChange(def.key, { showAwayImages: e.value })} />
							<label style={{ fontWeight: 600 }}>Away team</label>
						</div>
						<span style={{ color: "#6b7280", fontSize: 12 }}>Turn a side off when you don&rsquo;t hold photos for it.</span>
					</div>}
				{def.sponsorSlot && cfg.showSponsor &&
					<div style={{ marginBottom: 16, maxWidth: 420 }}>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Screen Sponsor</label>
						<Dropdown filter optionLabel="name" optionValue="id" options={this.state.sponsors} placeholder="Default sponsor for this screen" showClear style={{ width: "100%" }} value={cfg.sponsorId} onChange={(e) => this.screenChange(def.key, { sponsorId: e.value ?? null })} />
						<div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>Leave empty to use the screen&rsquo;s usual sponsor (main / additional-time / match sponsor).</div>
					</div>}
				{!cfg.useMainBackground && (
					<div style={{ marginBottom: 16, maxWidth: 420 }}>
						<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Screen Background</label>
						{cfg.backgroundUrl && <img alt="" src={cfg.backgroundUrl} style={{ display: "block", height: 60, marginBottom: 8, objectFit: "contain" }} />}
						<FileUpload accept="image/*" auto customUpload mode="basic" name={`${def.key}-bg`} uploadHandler={(data) => this.uploadScreenBackground(data, def.key)} />
						<InputText placeholder="…or paste a URL" style={{ display: "block", marginTop: 8, width: "100%" }} value={cfg.backgroundUrl || ""} onChange={(e) => this.screenChange(def.key, { backgroundUrl: e.target.value })} />
					</div>
				)}
				{def.elements.map((el) => (
					<div key={el.key} style={{ alignItems: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.08)", display: "flex", flexWrap: "wrap", gap: 14, padding: "10px 0" }}>
						<span style={{ fontWeight: 700, width: 190 }}>{el.label}</span>
						{this.screenNum(def.key, el.key, "x", "X")}
						{this.screenNum(def.key, el.key, "y", "Y")}
						{el.text
							? <>
								{this.screenNum(def.key, el.key, "fontSize", "Font")}
								{this.screenColor(def.key, el.key)}
							</>
							: <>
								{this.screenNum(def.key, el.key, "width", "W")}
								{this.screenNum(def.key, el.key, "height", "H")}
							</>}
					</div>
				))}
				<Button className="p-button-text" label="Reset this screen to defaults" style={{ marginTop: 8, padding: "6px 10px" }} onClick={() => {
					const screens = { ...this.state.theme.screens };
					delete screens[def.key];
					this.setState({ theme: { ...this.state.theme, screens } });
				}} />
			</div>
		);
	}

	imageSlot(label: string, property: keyof ScoreboardTheme) {
		const url = this.state.theme[property] as string;
		return (
			<div style={{ marginBottom: 20 }}>
				<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>{label}</label>
				{url && <img alt={label} src={url} style={{ display: "block", height: 60, marginBottom: 8, objectFit: "contain" }} />}
				<FileUpload accept="image/*" auto customUpload mode="basic" name={property} uploadHandler={(data) => this.uploadImage(data, property)} />
				<InputText placeholder="…or paste a URL" style={{ display: "block", marginTop: 8, width: "100%" }} value={url} onChange={(e) => this.inputChange(e.target.value, property)} />
			</div>
		);
	}

	render() {
		const { theme, sponsors, activeScreen } = this.state;
		return (
			<div style={{ padding: 20 }}>
				<Toast ref={(el) => (this.toast = el)} />
				<h2 style={{ marginBottom: 20 }}>Scoreboard Settings</h2>

				{this.state.configs.length > 0 &&
					<div style={{ alignItems: "center", background: "var(--pb-surface-2)", borderRadius: 14, display: "flex", flexWrap: "wrap", gap: 12, marginBottom: 22, padding: "14px 16px" }}>
						<span style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Layout</span>
						<Dropdown optionLabel="name" optionValue="id" options={this.state.configs} style={{ minWidth: 220 }} value={this.state.configId} onChange={(e) => this.selectConfig(e.value)} />
						{this.state.configs.find((c) => c.id === this.state.configId)?.isDefault
							? <span style={{ background: "var(--pb-emerald-soft)", borderRadius: 999, color: "var(--pb-emerald)", fontSize: 12, fontWeight: 700, padding: "4px 10px" }}>DEFAULT</span>
							: <Button className="p-button-text" label="Make default" style={{ padding: "4px 10px" }} onClick={() => this.makeDefault()} />}
						<span style={{ flex: 1 }} />
						<Button className="p-button-outlined" icon="pi pi-plus" label="New" onClick={() => this.setState({ showAddConfig: true, newConfigName: "", duplicateFrom: true })} />
						<Button className="p-button-text" icon="pi pi-pencil" label="Rename" onClick={() => this.renameConfig()} />
						<Button className="p-button-text p-button-danger" icon="pi pi-trash" label="Remove" onClick={() => this.removeConfig()} />
						<div style={{ color: "#6b7280", fontSize: 12, width: "100%" }}>
							Each scoreboard can use a different layout — assign them under System &rarr; Scoreboards.
						</div>
					</div>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showAddConfig: false })} />
					<Button icon="pi pi-check" label="Create layout" onClick={() => this.addConfig()} />
				</>} header="New layout" modal onHide={() => this.setState({ showAddConfig: false })} style={{ width: 420 }} visible={this.state.showAddConfig}>
					<label style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Name</label>
					<InputText autoFocus onChange={(e) => this.setState({ newConfigName: e.target.value })} placeholder="Concourse TV" style={{ marginBottom: 16, width: "100%" }} value={this.state.newConfigName} />
					<div style={{ alignItems: "center", display: "flex", gap: 12 }}>
						<InputSwitch checked={this.state.duplicateFrom} onChange={(e) => this.setState({ duplicateFrom: e.value })} />
						<label style={{ fontWeight: 600 }}>Copy the current layout</label>
					</div>
				</Dialog>

				<Row>
					<Col md={6}>
						{this.imageSlot("Main Background", "backgroundUrl")}
						{this.imageSlot("League Logo", "leagueLogoUrl")}
						{this.imageSlot("Additional Time Logo", "additionalTimeLogoUrl")}
					</Col>

					<Col md={6}>
						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Main Sponsor</label>
							<Dropdown filter optionLabel="name" optionValue="id" options={sponsors} placeholder="Select a sponsor" showClear style={{ width: "100%" }} value={theme.mainSponsorId} onChange={(e) => this.inputChange(e.value ?? null, "mainSponsorId")} />
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Additional Time Sponsor</label>
							<Dropdown filter optionLabel="name" optionValue="id" options={sponsors} placeholder="Select a sponsor" showClear style={{ width: "100%" }} value={theme.additionalTimeSponsorId} onChange={(e) => this.inputChange(e.value ?? null, "additionalTimeSponsorId")} />
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Additional Time Bar Colour</label>
							<div style={{ alignItems: "center", display: "flex", gap: 10 }}>
								<input type="color" value={theme.additionalTimeSponsorBg || "#2A3247"} onChange={(e) => this.inputChange(e.target.value, "additionalTimeSponsorBg")} />
								<InputText style={{ width: 140 }} value={theme.additionalTimeSponsorBg} onChange={(e) => this.inputChange(e.target.value, "additionalTimeSponsorBg")} />
							</div>
						</div>
					</Col>
				</Row>

				<h3 style={{ marginTop: 26, marginBottom: 16 }}>Display &amp; Rotation</h3>
				<Row>
					<Col md={6}>
						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Main Sponsor Bar Colour</label>
							<div style={{ alignItems: "center", display: "flex", gap: 10 }}>
								<input type="color" value={theme.mainSponsorBg || "#FFFFFF"} onChange={(e) => this.inputChange(e.target.value, "mainSponsorBg")} />
								<InputText style={{ width: 140 }} value={theme.mainSponsorBg} onChange={(e) => this.inputChange(e.target.value, "mainSponsorBg")} />
							</div>
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Text / Timer Colour</label>
							<div style={{ alignItems: "center", display: "flex", gap: 10 }}>
								<input type="color" value={theme.textColor || "#FFFFFF"} onChange={(e) => this.inputChange(e.target.value, "textColor")} />
								<InputText style={{ width: 140 }} value={theme.textColor} onChange={(e) => this.inputChange(e.target.value, "textColor")} />
							</div>
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Screen Cycle (seconds)</label>
							<InputNumber min={1} showButtons value={theme.screenCycleSeconds} onValueChange={(e) => this.inputChange(e.value ?? 5, "screenCycleSeconds")} />
							<div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>How fast Partners / Team / General rotate their items.</div>
						</div>
					</Col>

					<Col md={6}>
						<div style={{ alignItems: "center", display: "flex", gap: 12, marginBottom: 20 }}>
							<InputSwitch checked={theme.autoRotateEnabled} onChange={(e) => this.inputChange(e.value, "autoRotateEnabled")} />
							<label style={{ fontWeight: 600 }}>Auto-rotate screens when idle</label>
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Playlist</label>
							<MultiSelect display="chip" options={SCREEN_OPTIONS} placeholder="Pick screens to cycle" style={{ width: "100%" }} value={theme.autoRotateScreens} onChange={(e) => this.inputChange(e.value, "autoRotateScreens")} />
							<div style={{ color: "#6b7280", fontSize: 12, marginTop: 6 }}>Screens the board cycles through automatically. Any operator command pauses rotation for a minute.</div>
						</div>

						<div style={{ marginBottom: 20 }}>
							<label style={{ display: "block", fontWeight: 600, marginBottom: 8 }}>Dwell per screen (seconds)</label>
							<InputNumber min={3} showButtons value={theme.autoRotateDwellSeconds} onValueChange={(e) => this.inputChange(e.value ?? 15, "autoRotateDwellSeconds")} />
						</div>
					</Col>
				</Row>

				<h3 style={{ marginTop: 26, marginBottom: 6 }}>Screen Layouts</h3>
				<div style={{ color: "#6b7280", fontSize: 12, marginBottom: 12 }}>
					Positions in board-canvas pixels. Images anchor from their top-left corner; text anchors from its centre point. Changes apply to connected boards the moment you save.
				</div>
				<div className="pb-tabs" style={{ flexWrap: "wrap", marginBottom: 18 }}>
					<button className={`pb-tab${activeScreen === "main" ? " active" : ""}`} onClick={() => this.setState({ activeScreen: "main" })} type="button">Main Screen</button>
					{SCREEN_DEFS.map((d) => (
						<button className={`pb-tab${activeScreen === d.key ? " active" : ""}`} key={d.key} onClick={() => this.setState({ activeScreen: d.key })} type="button">{d.label}</button>
					))}
				</div>

				{activeScreen === "main" && <div className="sb-layout-split">
					<div className="sb-layout-rows">
					<div style={{ alignItems: "center", borderBottom: "1px solid rgba(255, 255, 255, 0.08)", display: "flex", flexWrap: "wrap", gap: 14, padding: "10px 0" }}>
						<span style={{ fontWeight: 700, width: 150 }}>Board Size</span>
						<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
							<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>W</label>
							<InputNumber inputStyle={{ width: 74 }} min={100} value={(theme.layout || DEFAULT_LAYOUT).canvasWidth} onValueChange={(e) => this.canvasChange("canvasWidth", e.value ?? 576)} />
						</div>
						<div style={{ alignItems: "center", display: "flex", gap: 6 }}>
							<label style={{ color: "#9DA3AF", fontSize: 12, fontWeight: 600 }}>H</label>
							<InputNumber inputStyle={{ width: 74 }} min={100} value={(theme.layout || DEFAULT_LAYOUT).canvasHeight} onValueChange={(e) => this.canvasChange("canvasHeight", e.value ?? 432)} />
						</div>
						<span style={{ color: "#6b7280", fontSize: 12 }}>The board&rsquo;s logical resolution — element positions use these pixels.</span>
					</div>
					{this.layoutRow("League Logo", "leagueLogo", false)}
					{this.layoutRow("Home Badge", "homeBadge", false)}
					{this.layoutRow("Away Badge", "awayBadge", false)}
					{this.layoutRow("Main Sponsor", "mainSponsor", false)}
					{this.layoutRow("Score", "score", true)}
					{this.layoutRow("Timer", "timer", true)}
					{this.layoutRow("Additional Time Text", "additionalTime", true)}
					<Button className="p-button-text" label="Reset layout to defaults" style={{ marginTop: 8, padding: "6px 10px" }} onClick={() => this.setState({ theme: { ...this.state.theme, layout: { ...DEFAULT_LAYOUT } } })} />
					</div>
					<div className="sb-layout-preview">
						<span className="sb-preview-label">Preview</span>
						{this.renderMainPreview()}
					</div>
				</div>}

				{SCREEN_DEFS.filter((d) => d.key === activeScreen).map((d) => (
					<div className="sb-layout-split" key={d.key}>
						<div className="sb-layout-rows">{this.renderScreenEditor(d)}</div>
						<div className="sb-layout-preview">
							<span className="sb-preview-label">Preview</span>
							{this.renderScreenPreview(d)}
						</div>
					</div>
				))}

				<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} style={{ display: "block", marginTop: 16 }} onClick={() => this.save()} />
			</div>
		);
	}
}
